import { useEffect, useState } from 'react'
import { motion } from 'framer-motion'
import { Link, useNavigate } from 'react-router-dom'
import { SectionBadge } from '../../components/SectionHeading'
import { ArrowCircleButton } from '../../components/Button'
import { apiGetSkiTransfers, shouldShowOnHomepage, type SkiTransfer } from '../../api/skiTransfers/skiTransferApi'

export default function SkiTransfers() {
  const navigate = useNavigate()
  const [transfers, setTransfers] = useState<SkiTransfer[]>([])

  useEffect(() => {
    async function loadTransfers() {
      const res = await apiGetSkiTransfers()
      if (res.data && res.data.length > 0) {
        const filtered = res.data.filter(shouldShowOnHomepage)
        setTransfers(filtered.length > 0 ? filtered : res.data)
      }
    }

    loadTransfers()
  }, [])

  const handleSelectTransfer = (transfer: SkiTransfer) => {
    const params = new URLSearchParams({
      serviceType: 'skitransfers',
      serviceId: transfer.id,
      serviceTitle: transfer.title,
      toTitle: transfer.title,
      toAddress: `${transfer.title}, Austria`,
    })
    if (transfer.base_price) {
      params.set('servicePrice', String(transfer.base_price))
    }

    window.dispatchEvent(
      new CustomEvent('salzburg-set-ski-destination', {
        detail: {
          id: transfer.id,
          title: transfer.title,
          address: `${transfer.title}, Austria`,
          price: transfer.base_price,
        },
      })
    )

    navigate(`/?${params.toString()}#transfers`, { replace: true })

    setTimeout(() => {
      document.getElementById('transfers')?.scrollIntoView({ behavior: 'smooth', block: 'center' })
    }, 60)
  }

  if (transfers.length === 0) return null

  return (
    <section className="mx-auto max-w-page px-4 py-16 lg:px-7 lg:py-20 overflow-hidden">
      <div className="flex flex-col gap-10 lg:flex-row lg:items-center lg:gap-16">
        <motion.div 
          initial={{ opacity: 0, x: -50 }}
          whileInView={{ opacity: 1, x: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.6, ease: "easeOut" }}
          className="max-w-[386px]"
        >
          <SectionBadge>Ski Transfers</SectionBadge>
          <h2 className="mt-3 font-host text-[28px] font-medium leading-[1.5] tracking-[-0.022em] text-black">
            Travel to Austria&apos;s Top Ski Resorts
          </h2>
          <p className="mt-4 font-host text-base leading-[1.37] text-black">
            Our ski transfer service provides comfortable transportation from Salzburg Airport,
            hotels, and city locations directly to popular ski resorts across Austria and
            neighboring regions.
          </p>
          <div className="mt-6 flex items-center gap-3">
            <Link
              to="/ski-transfers"
              className="inline-block rounded-[37px] border border-[#C8C8C8] bg-[#211F1F] px-8 py-3 text-center font-host text-base font-medium text-white transition-all duration-200 hover:-translate-y-0.5 hover:bg-[#2f2d2d] active:scale-[0.98]"
            >
              Book Ski Transfer
            </Link>
            <ArrowCircleButton to="/ski-transfers" aria-label="Go to ski transfers" />
          </div>
        </motion.div>
        <div className="flex flex-1 flex-col gap-4 sm:flex-row">
          {transfers.slice(0, 2).map((transfer, index) => (
            <motion.div
              key={transfer.id}
              initial={{ opacity: 0, y: 50 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: '-100px' }}
              transition={{ duration: 0.6, ease: 'easeOut', delay: 0.2 + index * 0.2 }}
              className={
                index === 0
                  ? 'h-[364px] flex-1 overflow-hidden rounded-[33px]'
                  : 'h-[215px] flex-1 overflow-hidden rounded-[26px] sm:max-w-[410px]'
              }
            >
              <button
                type="button"
                onClick={() => handleSelectTransfer(transfer)}
                className="group relative block h-full w-full overflow-hidden text-left cursor-pointer"
                title={`Book ski transfer to ${transfer.title}`}
              >
                <img
                  src={transfer.image || '/assets/images/ski-main.png'}
                  alt={transfer.title}
                  className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-105"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-80 transition-opacity group-hover:opacity-90" />
                <div className="absolute bottom-4 left-5 right-5 text-white">
                  <p className="font-host text-[18px] font-bold leading-tight drop-shadow-sm">
                    {transfer.title}
                  </p>
                  <p className="mt-1 font-host text-[13px] text-white/90">
                    Click to select destination
                  </p>
                </div>
              </button>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  )
}
